/* eslint-disable @typescript-eslint/no-explicit-any */
import { Col, Row } from '@design-system/components/grid/grid';

import { TitleRow } from '../../promotions/view/components/FormTypography';
import { Typography } from '@design-system/components/typography/typography';
import { wrapper } from './components/ChallengeForm.css';
import { ChallengeFormVM } from '../view-model/ChallengeForm.viewModel';
import { TemplatesList } from './components/TemplatesList';
import { ToggleDescription } from './components/ToggleDescription';
import { PropsWithChildren } from 'react';
import { borderRadius, rgb, space } from '@design-system/tokens';
import { formatDateWithTime } from '../../promotions/view-model/utils';
import { createDate, DateTime } from '../view-model/utils';

const Group = (p: PropsWithChildren<{ title: string }>) => {
  return (
    <Col
      gap="l"
      style={{ borderTop: `1px solid ${rgb('N-40')}`, paddingTop: space.xl }}
    >
      <Typography variant="body" size="large" fontWeight="medium">
        {p.title}
      </Typography>
      <Col>{p.children}</Col>
    </Col>
  );
};

const toDate = (d: DateTime) =>
  formatDateWithTime(
    createDate(d),
    '',
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );

export const ChallengePreview = ({
  register,
  values,
  ...p
}: ChallengeFormVM) => {
  return (
    <Col data-testid="ChallengeDetails" className={wrapper} gap="xl">
      <TitleRow title={'Challenge Details'} />

      <Group title="Registration & before photo submission">
        <Col gap="l">
          <Row
            style={{
              background: rgb('N-20'),
              fontWeight: 600,
              fontSize: '14px',
              padding: `${space.s} ${space.l}`,
              borderRadius: borderRadius.xl,
              width: 'fit-content',
            }}
          >
            Registration {toDate(values.registrationStart as DateTime)} -{' '}
            {toDate(values.registrationEnd as DateTime)}
          </Row>
          <Typography variant="body" size="large" fontWeight="medium">
            Required fields
          </Typography>
          <Col>
            <Row gap="xl" style={{ opacity: 0.3, pointerEvents: 'none' }}>
              {p.submissionToggles.map((option) => (
                <ToggleDescription
                  key={option.value}
                  {...option}
                  {...register(option.value)}
                />
              ))}
            </Row>
          </Col>
        </Col>
      </Group>

      <Group title="Completion & after photo submission">
        <Col gap="l">
          <Row
            style={{
              background: rgb('N-20'),
              fontWeight: 600,
              fontSize: '14px',
              padding: `${space.s} ${space.l}`,
              borderRadius: borderRadius.xl,
              width: 'fit-content',
            }}
          >
            Completion {toDate(values.completionStart as DateTime)} -{' '}
            {toDate(values.completionEnd as DateTime)}
          </Row>

          <Typography variant="body" size="large" fontWeight="medium">
            Required fields
          </Typography>

          <Col>
            <Row gap="xl" style={{ opacity: 0.3, pointerEvents: 'none' }}>
              {p.completionToggles.map((option) => (
                <ToggleDescription
                  key={option.label}
                  {...option}
                  {...register(option.value)}
                />
              ))}
            </Row>
          </Col>
        </Col>
      </Group>

      <Group title="Photos section visibility">
        <Col style={{ opacity: 0.3, pointerEvents: 'none' }}>
          <ToggleDescription
            label="Show section"
            {...register('showPhotosSection')}
          />
        </Col>
      </Group>

      <Group title="Photo Guidelines">
        <Col>
          {values.photoInstructions ? (
            <Typography size="medium" color="N-80" fontWeight="normal">
              {values.photoInstructions}
            </Typography>
          ) : (
            <Typography size="medium" fontWeight="normal">
              No instructions included
            </Typography>
          )}
        </Col>
      </Group>

      <Group title="Templates">
        {p.templates.length ? (
          <TemplatesList urls={p.templates} />
        ) : (
          <Typography size="medium" fontWeight="normal">
            No templates included
          </Typography>
        )}
      </Group>
    </Col>
  );
};
